import AddIcon from "@mui/icons-material/Add";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import MDButton from "components/MDButton";
import { useState } from "react";

const ADD_NEW_OPTION = {
  id: "__pathology_add_new__",
  name: "+ Add new disease",
  isAddOption: true,
};

const defaultFilter = createFilterOptions();

function diseaseLabel(d) {
  if (!d || d.isAddOption) return "+ Add new disease";
  const status =
    d.status === "published" ? " ✓" : d.status === "draft" ? " (draft)" : "";
  const severity = d.severity ? ` · ${d.severity}` : "";
  return `${d.name}${severity}${status}`;
}

export default function PathologyDiseaseSelect({
  diseases = [],
  selectedDiseaseId,
  onSelectDiseaseId,
  newName,
  setNewName,
  newDescription,
  setNewDescription,
  newSeverity,
  setNewSeverity,
  onCreateDisease,
  loading,
  severities = ["mild", "moderate", "severe"],
}) {
  const [addOpen, setAddOpen] = useState(false);

  const selectedDisease =
    diseases.find((d) => d.id === selectedDiseaseId) || null;

  const filterOptions = (options, params) => {
    const real = options.filter((o) => !o.isAddOption);
    const filtered = defaultFilter(real, params);
    return [...filtered, ADD_NEW_OPTION];
  };

  const openAddPanel = () => {
    setAddOpen(true);
    onSelectDiseaseId("");
  };

  const handleCreate = async () => {
    await onCreateDisease();
    setAddOpen(false);
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={0.5} alignItems="flex-start">
        <Autocomplete
          size="small"
          fullWidth
          disablePortal={false}
          options={diseases}
          value={selectedDisease}
          filterOptions={filterOptions}
          getOptionLabel={diseaseLabel}
          isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
          onChange={(_, option) => {
            if (!option) {
              onSelectDiseaseId("");
              setAddOpen(false);
              return;
            }
            if (option.isAddOption || option.id === ADD_NEW_OPTION.id) {
              openAddPanel();
              return;
            }
            setAddOpen(false);
            onSelectDiseaseId(option.id);
          }}
          slotProps={{
            popper: {
              sx: { zIndex: 10000 },
              placement: "bottom-start",
              modifiers: [{ name: "flip", enabled: true }],
            },
            paper: {
              sx: { maxHeight: 280 },
            },
          }}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            const isAdd = option.isAddOption || option.id === ADD_NEW_OPTION.id;
            return (
              <li
                key={key}
                {...rest}
                style={{
                  ...(rest.style || {}),
                  ...(isAdd
                    ? {
                        fontWeight: 600,
                        color: "#1976d2",
                        borderTop: "1px solid rgba(0,0,0,0.08)",
                        marginTop: 4,
                        paddingTop: 8,
                      }
                    : {}),
                }}
              >
                {isAdd ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AddIcon fontSize="small" />
                    Add new disease
                  </Box>
                ) : (
                  diseaseLabel(option)
                )}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Disease"
              placeholder={
                diseases.length
                  ? "Select disease…"
                  : "No diseases yet — add one"
              }
            />
          )}
          noOptionsText={
            <MDButton
              variant="text"
              color="info"
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={openAddPanel}
            >
              <AddIcon sx={{ mr: 0.5, fontSize: 16 }} />
              Add new disease
            </MDButton>
          }
        />
        <Tooltip title="Add new disease">
          <IconButton
            size="small"
            color="primary"
            onClick={openAddPanel}
            sx={{ mt: 0.25 }}
            aria-label="Add new disease"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {selectedDisease?.description ? (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          {selectedDisease.description}
        </Typography>
      ) : null}

      <Collapse in={addOpen}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" fontWeight={700} display="block" mb={1}>
            New disease
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              size="small"
              label="Disease name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Mild Hepatic Steatosis"
              autoFocus
            />
            <TextField
              size="small"
              label="Description"
              multiline
              minRows={2}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />
            <FormControl>
              <FormLabel sx={{ fontSize: 12 }}>Severity</FormLabel>
              <RadioGroup
                row
                value={newSeverity}
                onChange={(e) => setNewSeverity(e.target.value)}
              >
                {severities.map((s) => (
                  <FormControlLabel
                    key={s}
                    value={s}
                    control={<Radio size="small" />}
                    label={s.charAt(0).toUpperCase() + s.slice(1)}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <Stack direction="row" spacing={1}>
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                onClick={handleCreate}
                disabled={loading || !newName.trim()}
              >
                Create
              </MDButton>
              <MDButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => setAddOpen(false)}
              >
                Cancel
              </MDButton>
            </Stack>
          </Stack>
        </Box>
      </Collapse>

      {!addOpen && diseases.length === 0 ? (
        <>
          <Divider />
          <Typography variant="caption" color="text.secondary">
            Use the dropdown or + button to add your first disease for this slide.
          </Typography>
        </>
      ) : null}
    </Stack>
  );
}
